'use client'

import { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Icon, Text } from '@vinted/web-ui'
import { ArrowLeft24 } from '@vinted/monochrome-icons'

import { getNewPriceRange, PriceRange } from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogConfigurationContext,
  useCatalogContext,
} from '@marketplace-web/catalog/catalog-page-data'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { CurrencyAmountModel, formatCurrency } from '@marketplace-web/currency/currency-data'

import FilterCell from '../../common/FilterCell'
import FilterModal from '../../common/FilterModal'
import FilterModalNavigation from '../../common/FilterModalNavigation'
import PriceRangeInput from '../../common/PriceRangeInput'

const FALLBACK_PRICE_FROM_VALUE = '0'

type Props = {
  selectedPriceRange: PriceRange
  isFilterCellVisible: boolean
  minimumValidation?: CurrencyAmountModel
  onPriceRangeChange: (range: PriceRange) => void
  onSubmit: () => void
  onFilterCellClick: () => void
  onFilterCellView: () => void
  onModalOpen: () => void
  onModalClose: () => void
}

const PriceFilter = ({
  selectedPriceRange,
  isFilterCellVisible,
  minimumValidation,
  onPriceRangeChange,
  onSubmit,
  onFilterCellClick,
  onFilterCellView,
  onModalOpen,
  onModalClose,
}: Props) => {
  const translate = useTranslate('catalog')
  const { formatNumber, locale } = useIntl()
  const {
    state: { filters },
  } = useCatalogContext()
  const serverConfiguration = useCatalogConfigurationContext()
  const configuration = serverConfiguration
  const currency = filters.currency || configuration.config.currency

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [shouldUpdatePrice, setShouldUpdatePrice] = useState(false)

  const closeModal = () => {
    setIsModalOpen(false)
    onModalClose()
  }

  const openModal = () => {
    setIsModalOpen(true)
    setShouldUpdatePrice(true)
    onModalOpen()
  }

  const handleSubmitClick = () => {
    closeModal()
    onSubmit()
  }

  const clearPriceRange = () => {
    onPriceRangeChange({ priceFrom: null, priceTo: null })
    setShouldUpdatePrice(true)
  }

  const handlePriceRangeChange = ({ priceFrom, priceTo }: PriceRange) => {
    const newPriceFrom = priceTo ? priceFrom || FALLBACK_PRICE_FROM_VALUE : priceFrom

    setShouldUpdatePrice(true)
    onPriceRangeChange(getNewPriceRange(newPriceFrom, priceTo))
  }

  const handleFilterCellClick = () => {
    openModal()
    onFilterCellClick()
  }

  const renderModalNavigation = () => (
    <FilterModalNavigation
      title={translate('filters.price.name')}
      suffixText={translate('filters.price.clear_price')}
      prefixAriaLabel={translate('filters.a11y.actions.back')}
      icon={<Icon name={ArrowLeft24} testId="price-navigation-prefix-icon" />}
      onPrefixClick={closeModal}
      onSuffixClick={clearPriceRange}
    />
  )

  const renderPriceFilterModal = () => (
    <FilterModal
      renderNavigation={renderModalNavigation}
      isOpen={isModalOpen}
      onSubmit={handleSubmitClick}
      testId="price-modal"
    >
      <div className="u-flexbox">
        <PriceRangeInput
          priceRange={selectedPriceRange}
          priceFromTitle={translate('filters.price.price_from')}
          priceToTitle={translate('filters.price.price_to')}
          onPriceRangeChange={handlePriceRangeChange}
          shouldUpdateInputValues={shouldUpdatePrice}
          minimumValidation={minimumValidation}
          shouldFocusInput
        />
      </div>
      {minimumValidation && (
        <div className="u-ui-padding-horizontal-medium u-ui-padding-bottom-regular">
          <Text
            type={Text.Type.Caption}
            theme="muted"
            as="span"
            text={translate('filters.price.designer_price_message', {
              value: formatCurrency(minimumValidation.amount, { locale, currency }),
            })}
          />
        </div>
      )}
    </FilterModal>
  )

  const formatCurrencyValue = (value: string) => {
    const numericValue = parseFloat(value)

    return formatNumber(numericValue, { style: 'currency', currency })
  }

  const getSelectedPriceRangeText = () => {
    const { priceFrom, priceTo } = selectedPriceRange

    if (priceFrom && priceTo) {
      return translate('selected_filters.both_price', {
        priceFrom: formatCurrencyValue(priceFrom),
        priceTo: formatCurrencyValue(priceTo),
      })
    }
    if (priceFrom) {
      return translate('selected_filters.price_from', { value: formatCurrencyValue(priceFrom) })
    }
    if (priceTo) {
      return translate('selected_filters.price_to', { value: formatCurrencyValue(priceTo) })
    }

    return null
  }

  const suffixText = getSelectedPriceRangeText()

  useEffect(() => {
    const { priceFrom, priceTo } = selectedPriceRange

    if (minimumValidation?.amount) {
      const minAmount = parseFloat(minimumValidation.amount)
      let priceFromValue = priceFrom
      let priceToValue = priceTo

      if (priceFrom && parseFloat(priceFrom) < minAmount) {
        if (priceTo && parseFloat(priceTo) >= minAmount) {
          priceFromValue = minimumValidation.amount.toString()
        } else {
          priceFromValue = null
        }
      }

      if (priceTo && parseFloat(priceTo) < minAmount) {
        priceToValue = null
      }

      const priceRange = {
        priceFrom: priceFromValue,
        priceTo: priceToValue,
      }

      handlePriceRangeChange(priceRange)
    }
    // to avoid endless loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <FilterCell
        title={translate('filters.price.name')}
        suffixText={suffixText || translate('filters.price.all')}
        suffixTextTheme={suffixText ? 'primary' : undefined}
        onClick={handleFilterCellClick}
        onView={onFilterCellView}
        visible={isFilterCellVisible}
      />
      {renderPriceFilterModal()}
    </>
  )
}

export default PriceFilter
